import { EMPTY_TILE, packedToTileRef } from "../document/packed-tile.ts";
import type { EntityInstance, MapDocument, PropInstance, TileRef, WorldDocument } from "../document/types.ts";
import { anchoredIn, assertAreaRect, areaGridBounds, areaGridSize, type AreaRect } from "./area.ts";
import { eachCell, isGridLayer, paintedIn, rawGridOf } from "./area-grids.ts";
import { areaMapOf } from "./area-move.ts";

/**
 * The area's clipboard (ADR-128 decisions 37 to 39): what the layers an area takes held inside its rectangle, as
 * plain data with no reference into the document, so it can be kept, compared and sent as JSON. A tile layer's
 * cells are tile references (a renumbering of the world's tilesets changes no cell's meaning), a terrain layer's
 * the corner rectangle's bytes, a collision layer's its quarter cells, a zone layer's the type ids of its quarter
 * cells; a prop layer's and a marker layer's items are whole, each with its point relative to the rectangle's
 * top-left corner. Door cells are never copied (decision 38): a second patch of the same door somewhere else is
 * never what a copy means, so a door layer has no entry and the cells it would have held are only counted. A grid
 * layer is in the clipboard only when it paints at least one cell inside the rectangle, so copying a hut does not
 * make the paste clear the zones of the ground it is put on.
 */

/**
 * A tile layer's block: its cells row by row, empty cells as null.
 */
export interface AreaClipTiles {
  readonly kind: "tile";
  readonly layerId: string;
  readonly tiles: readonly (TileRef | null)[];
}

/**
 * A terrain layer's block: the corners of columns `x0` to `x1 + 1` and rows `y0` to `y1 + 1`, row by row.
 */
export interface AreaClipCorners {
  readonly kind: "terrain";
  readonly layerId: string;
  readonly corners: readonly number[];
}

/**
 * A collision layer's block: the quarter cells of the rectangle's tiles, row by row.
 */
export interface AreaClipCollision {
  readonly kind: "collision";
  readonly layerId: string;
  readonly cells: readonly number[];
}

/**
 * A zone layer's block: the quarter cells of the rectangle's tiles, row by row, each a zone type id or null.
 */
export interface AreaClipZone {
  readonly kind: "zone";
  readonly layerId: string;
  readonly cells: readonly (string | null)[];
}

/**
 * A prop layer's block: each prop anchored inside, whole, with its point's distance from the rectangle's top-left
 * corner in tiles. The prop keeps the id it had, which is how a reference between two copied things is found.
 */
export interface AreaClipProps {
  readonly kind: "prop";
  readonly layerId: string;
  readonly props: readonly { readonly dx: number; readonly dy: number; readonly prop: PropInstance }[];
}

/**
 * A marker layer's block: each marker anchored inside, whole, with its point's distance from the corner.
 */
export interface AreaClipMarkers {
  readonly kind: "entity";
  readonly layerId: string;
  readonly entities: readonly { readonly dx: number; readonly dy: number; readonly entity: EntityInstance }[];
}

/**
 * One layer's block.
 */
export type AreaClipLayer = AreaClipTiles | AreaClipCorners | AreaClipCollision | AreaClipZone | AreaClipProps | AreaClipMarkers;

/**
 * The clipboard: the map it was taken from, the rectangle it was taken at, its size in tiles, each layer's block in
 * the map's order and how many door cells the rectangle held on the door layers taken, which were left out.
 */
export interface AreaClipboard {
  readonly mapId: string;
  readonly origin: AreaRect;
  readonly width: number;
  readonly height: number;
  readonly layers: readonly AreaClipLayer[];
  readonly doorCells: number;
}

/**
 * What a paste of the clipboard makes new, for the bar to say (decision 38): props that carry a behaviour (new
 * things that start in their initial state, with nothing remembered of the originals), arrival points (nothing leads to them) and the door
 * cells left out of the copy.
 */
export interface AreaClipboardNotes {
  readonly behaviours: number;
  readonly arrivals: number;
  readonly doorCells: number;
}

/**
 * Copies what the layers an area takes hold inside its rectangle.
 *
 * @param world The world as it stands.
 * @param mapId The map in view.
 * @param rect The area's rectangle of tiles.
 * @param layerIds The layers the reach takes (`areaReach`); only these are read.
 * @returns The clipboard, or null when the taken layers hold nothing to copy there (door cells do not count).
 * @throws Error when the map is missing or a zone byte names no type of its layer; RangeError when the rectangle is
 *   not one of whole tiles inside the map.
 */
export function copyArea(world: WorldDocument, mapId: string, rect: AreaRect, layerIds: readonly string[]): AreaClipboard | null {
  const map = areaMapOf(world, mapId);
  assertAreaRect(map, rect);
  const taken = new Set(layerIds);
  const layers: AreaClipLayer[] = [];
  let doorCells = 0;
  for (const layer of map.layers) {
    if (!taken.has(layer.id)) {
      continue;
    }
    if (layer.kind === "prop") {
      const props = layer.props.filter((p) => anchoredIn(rect, p.x, p.y)).map((prop) => ({ dx: prop.x - rect.x0, dy: prop.y - rect.y0, prop }));
      if (props.length > 0) {
        layers.push({ kind: "prop", layerId: layer.id, props });
      }
      continue;
    }
    if (layer.kind === "entity") {
      const entities = layer.entities.filter((e) => anchoredIn(rect, e.x, e.y)).map((entity) => ({ dx: entity.x - rect.x0, dy: entity.y - rect.y0, entity }));
      if (entities.length > 0) {
        layers.push({ kind: "entity", layerId: layer.id, entities });
      }
      continue;
    }
    if (!isGridLayer(layer)) {
      continue;
    }
    const raw = rawGridOf(layer);
    const { width } = areaGridSize(map, raw.grid);
    const cells: number[] = [];
    eachCell(areaGridBounds(rect, raw.grid), width, (index) => {
      cells.push(raw.cells[index] as number);
    });
    const painted = cells.reduce((count, value) => count + (value === raw.empty ? 0 : 1), 0);
    if (layer.kind === "door") {
      doorCells += painted;
    } else if (painted > 0) {
      if (layer.kind === "tile") {
        layers.push({ kind: "tile", layerId: layer.id, tiles: cells.map((packed) => (packed === EMPTY_TILE ? null : packedToTileRef(packed, world.tilesets))) });
      } else if (layer.kind === "terrain") {
        layers.push({ kind: "terrain", layerId: layer.id, corners: cells });
      } else if (layer.kind === "collision") {
        layers.push({ kind: "collision", layerId: layer.id, cells });
      } else {
        const types = layer.types;
        layers.push({
          kind: "zone",
          layerId: layer.id,
          cells: cells.map((value) => {
            if (value === 0) {
              return null;
            }
            const type = types[value - 1];
            if (type === undefined) {
              throw new Error(`zone layer ${layer.id} holds byte ${value}, which its table of ${types.length} types does not name`);
            }
            return type;
          }),
        });
      }
    }
  }
  if (layers.length === 0) {
    return null;
  }
  return { mapId, origin: rect, width: rect.x1 - rect.x0 + 1, height: rect.y1 - rect.y0 + 1, layers, doorCells };
}

/**
 * What a paste of the clipboard makes new.
 *
 * @param clipboard The clipboard.
 * @param arrivalType The entity type the game treats as an arrival point, absent when it names none.
 * @returns The counts.
 */
export function areaClipboardNotes(clipboard: AreaClipboard, arrivalType: string | undefined): AreaClipboardNotes {
  let behaviours = 0;
  let arrivals = 0;
  for (const layer of clipboard.layers) {
    if (layer.kind === "prop") {
      behaviours += layer.props.filter((p) => p.prop.behaviour !== undefined).length;
    } else if (layer.kind === "entity" && arrivalType !== undefined) {
      arrivals += layer.entities.filter((e) => e.entity.type === arrivalType).length;
    }
  }
  return { behaviours, arrivals, doorCells: clipboard.doorCells };
}

/**
 * How many door cells the taken door layers paint inside a rectangle, which a copy leaves out.
 *
 * @param map The map.
 * @param rect The rectangle of tiles.
 * @param layerIds The layers the reach takes.
 * @returns The count.
 */
export function areaDoorCells(map: MapDocument, rect: AreaRect, layerIds: readonly string[]): number {
  const taken = new Set(layerIds);
  return map.layers.reduce((count, layer) => count + (layer.kind === "door" && taken.has(layer.id) ? paintedIn(map, layer, rect) : 0), 0);
}
